import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiErrorDetail } from '../../lib/adminApi';
import { ImageUpload } from './ImageUpload';

const CATEGORIES = ['Fashion', 'Beauty', 'Planning', 'Inspiration', 'Tips', 'Lifestyle'];
const EMPTY = { title: '', category: 'Inspiration', date: '', excerpt: '', content: '', image: '' };
const inputCls = 'w-full border border-[#E6E2DC] px-4 py-2.5 text-sm focus:outline-none focus:border-[#DE92A9] bg-white';
const labelCls = 'block text-xs tracking-wider uppercase text-[#757B7F] mb-1.5';

export default function ArticlesManager() {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/api/articles').then((res) => setArticles(res.data)).catch(() => toast.error('Failed to load articles'));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const now = new Date();
    setForm({ ...EMPTY, date: now.toLocaleDateString('en-GB', { month: 'short', day: '2-digit' }) });
    setEditing('new');
  };
  const openEdit = (a) => {
    setForm({ title: a.title, category: a.category, date: a.date, excerpt: a.excerpt, content: a.content || '', image: a.image });
    setEditing(a);
  };

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!form.image) { toast.error('Please upload an article image'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/api/admin/articles', form);
        toast.success('Article created');
      } else {
        await api.put(`/api/admin/articles/${editing.slug || editing.id}`, form);
        toast.success('Article updated');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}" permanently?`)) return;
    try {
      await api.delete(`/api/admin/articles/${a.slug || a.id}`);
      toast.success('Article deleted');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Delete failed');
    }
  };

  if (editing) {
    return (
      <form onSubmit={save} className="bg-white border border-[#E6E2DC]/60 p-8 max-w-3xl space-y-5" data-testid="article-form">
        <h2 className="text-2xl text-[#2C2C2C]" style={{ fontFamily: '"Playfair Display", serif' }}>
          {editing === 'new' ? 'New Article' : 'Edit Article'}
        </h2>
        <div>
          <label className={labelCls}>Title *</label>
          <input className={inputCls} value={form.title} onChange={set('title')} required data-testid="article-title-input" />
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Category *</label>
            <select className={inputCls} value={form.category} onChange={set('category')} data-testid="article-category-select">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Date label *</label>
            <input className={inputCls} value={form.date} onChange={set('date')} required placeholder="Jun 26" data-testid="article-date-input" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Excerpt *</label>
          <textarea className={inputCls} rows={3} value={form.excerpt} onChange={set('excerpt')} required data-testid="article-excerpt-input" />
        </div>
        <ImageUpload label="Article image *" value={form.image} onChange={(url) => setForm({ ...form, image: url })} testId="article-image-upload" />
        <div>
          <label className={labelCls}>Content (plain text or HTML)</label>
          <textarea className={`${inputCls} font-mono text-xs`} rows={12} value={form.content} onChange={set('content')} data-testid="article-content-input" />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} className="text-white text-xs tracking-[0.15em] font-semibold px-8 py-3 hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#DE92A9' }} data-testid="article-save-btn">
            {saving ? 'SAVING...' : 'SAVE ARTICLE'}
          </button>
          <button type="button" onClick={() => setEditing(null)} className="border border-[#E6E2DC] text-xs tracking-wider px-6 py-3 text-[#757B7F] hover:border-[#DE92A9]" data-testid="article-cancel-btn">
            CANCEL
          </button>
        </div>
      </form>
    );
  }

  return (
    <div data-testid="articles-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl text-[#2C2C2C]" style={{ fontFamily: '"Playfair Display", serif' }}>Articles</h2>
        <button onClick={openNew} className="inline-flex items-center gap-2 text-white text-xs tracking-wider font-semibold px-5 py-2.5 hover:opacity-90" style={{ backgroundColor: '#DE92A9' }} data-testid="article-add-btn">
          <Plus size={14} /> ADD ARTICLE
        </button>
      </div>
      <div className="bg-white border border-[#E6E2DC]/60 divide-y divide-[#E6E2DC]/60">
        {articles.map((a) => (
          <div key={a.id} className="flex items-center gap-4 p-4" data-testid={`article-row-${a.slug}`}>
            <img src={a.image} alt={a.title} className="w-20 h-14 object-cover border border-[#E6E2DC] shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-[#2C2C2C] font-medium truncate">{a.title}</p>
              <p className="text-xs text-[#757B7F]">{a.category} · {a.date}</p>
            </div>
            <button onClick={() => openEdit(a)} className="p-2 text-[#757B7F] hover:text-[#DE92A9]" data-testid={`article-edit-${a.slug}`} aria-label="Edit"><Pencil size={15} /></button>
            <button onClick={() => remove(a)} className="p-2 text-[#757B7F] hover:text-red-500" data-testid={`article-delete-${a.slug}`} aria-label="Delete"><Trash2 size={15} /></button>
          </div>
        ))}
        {articles.length === 0 && <p className="p-6 text-sm text-[#757B7F]">No articles yet.</p>}
      </div>
    </div>
  );
}
